/**
 * Elite Batch 6 Part 5 — Workflow Templates 528–547
 * Stale Listing, Browse Abandonment, Cart Abandonment, Replenishment, Store Review,
 * BOPIS Recovery, Stripe Payment, Subscription Management, Supplier Automation,
 * Thank-You, Trade-In, Trial-to-Paid, UGC Requests
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function shopifyNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://{shopify_store}.myshopify.com/admin/api/2024-01/${endpoint}`,
        headers: [
          { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendSMS",
    label,
    y,
    { sendSMSDetails: { to, content: body } },
    x,
  );
}
function stripeNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://api.stripe.com/v1/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {stripe_secret_key}" },
          { key: "Content-Type", value: "application/x-www-form-urlencoded" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function postmarkNode(
  id: string,
  label: string,
  y: number,
  to: string,
  templateId: string,
  templateModel: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.postmarkapp.com/email/withTemplate",
        headers: [
          { key: "X-Postmark-Server-Token", value: "{postmark_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "From", value: "{from_email}" },
          { key: "To", value: to },
          { key: "TemplateId", value: templateId },
          { key: "TemplateModel", value: templateModel },
        ],
        variables: [
          {
            name: "MessageID",
            customName: "postmark_message_id",
            type: "string",
          },
          {
            name: "ErrorCode",
            customName: "postmark_error_code",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 6 PART 5 — TEMPLATES 528–547
// =============================================================================

export const ELITE_BATCH6_PART5: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 528. STALE LISTING CLEANUP (8 nodes)
  // ===================================================================

  "Stale Listing Cleanup": {
    summary:
      "Weekly audit of e-commerce listings to identify stale products and queue them for delisting. A scheduled weekly cron triggers a Shopify listing metrics pull, applies staleness rules via runCode, generates AI optimization suggestions, routes on severity, emails a stale report, sends a Slack summary, and logs the delist review queue to Sheets.",
    nodes: [
      trigger("Schedule: Weekly listing audit", "schedule", {
        schedule: "0 6 * * 1",
        howItStarts:
          "Runs every Monday at 6 AM to audit all active product listings for staleness based on views, sales, and age",
        setup:
          "Configure Shopify API credentials. Set staleness thresholds (e.g., 0 sales in 60 days, <10 views in 30 days). Set up Google Sheets for delist review queue.",
        inputFields: [],
      }),

      // 2. Pull listing metrics from Shopify
      shopifyNode(
        "2",
        "Pull listing metrics (Shopify)",
        280,
        "GET",
        "products.json?status=active&fields=id,title,created_at,updated_at,variants,images&limit=250",
        [],
        [
          {
            name: "products.length",
            customName: "total_listings",
            type: "number",
          },
          { name: "products", customName: "products_payload", type: "string" },
        ],
      ),

      // 3. Apply staleness rules (runCode)
      n("3", "runCode", "Apply staleness rules", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Evaluate each listing against staleness criteria
const products = JSON.parse(vars.products_payload || "[]");
const now = Date.now();
const DAY = 86400000;
const staleThresholdDays = 60;
const lowViewThreshold = 10;
const stale = [];
const healthy = [];
for (const p of products) {
  const ageDays = Math.floor((now - new Date(p.created_at).getTime()) / DAY);
  const updatedDays = Math.floor((now - new Date(p.updated_at).getTime()) / DAY);
  const hasImages = (p.images || []).length > 0;
  const variantCount = (p.variants || []).length;
  const severity = updatedDays > staleThresholdDays && ageDays > 90 ? "critical" : updatedDays > staleThresholdDays ? "warning" : "ok";
  if (severity !== "ok") {
    stale.push({ id: p.id, title: p.title, age_days: ageDays, last_updated_days: updatedDays, has_images: hasImages, variant_count: variantCount, severity });
  } else {
    healthy.push(p.id);
  }
}
const criticalCount = stale.filter(s => s.severity === "critical").length;
const warningCount = stale.filter(s => s.severity === "warning").length;
return {
  stale_count: stale.length,
  critical_count: criticalCount,
  warning_count: warningCount,
  healthy_count: healthy.length,
  stale_listings: JSON.stringify(stale.slice(0, 50)),
  worst_severity: criticalCount > 0 ? "critical" : warningCount > 0 ? "warning" : "none",
  audit_summary: criticalCount + " critical, " + warningCount + " warnings out of " + products.length + " listings"
};`,
          outputVariables: [
            { name: "stale_count", type: "number" },
            { name: "critical_count", type: "number" },
            { name: "warning_count", type: "number" },
            { name: "healthy_count", type: "number" },
            { name: "stale_listings", type: "string" },
            { name: "worst_severity", type: "string" },
            { name: "audit_summary", type: "string" },
          ],
        },
      }),

      // 4. AI optimization suggestions
      aiNode(
        "4",
        "AI optimization suggestions",
        760,
        "You are an e-commerce listing optimization expert. Given a list of stale product listings with their age, update recency, and image status, generate actionable optimization suggestions for each. For critical items, recommend immediate delisting or deep refresh. For warnings, suggest title/description improvements, image additions, or price adjustments. Respond JSON: {suggestions: [{product_id: string, title: string, severity: string, action: string, reasoning: string}], total_suggestions: number, priority_actions: string}",
        "Stale listings data: {stale_listings}. Audit summary: {audit_summary}",
        [
          {
            field: "suggestions",
            as: "optimization_suggestions",
            type: "string",
          },
          {
            field: "total_suggestions",
            as: "suggestion_count",
            type: "number",
          },
          { field: "priority_actions", as: "priority_actions", type: "string" },
        ],
      ),

      // 5. Severity condition
      n("5", "condition", "Any critical stale listings?", 1000, {
        conditionData: [
          {
            variable: "worst_severity",
            value: "critical",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Stale report email
      emailNode(
        "6",
        "Send stale listing report",
        1240,
        "{ops_email}",
        "Weekly Stale Listing Report - {critical_count} Critical, {warning_count} Warnings",
        "Weekly Listing Audit Results\\n\\nTotal Listings Scanned: {total_listings}\\nHealthy: {healthy_count}\\nWarnings: {warning_count}\\nCritical: {critical_count}\\n\\nPriority Actions:\\n{priority_actions}\\n\\nPlease review the delist queue in Google Sheets and take action on critical listings within 48 hours.",
      ),

      // 7. Slack summary
      slackNode(
        "7",
        "Slack: Weekly stale audit summary",
        1480,
        ":mag: Weekly Listing Audit Complete\\nTotal: {total_listings} | Healthy: {healthy_count} | Warnings: {warning_count} | Critical: {critical_count}\\nPriority: {priority_actions}\\nFull report sent to ops email.",
        400,
      ),

      // 8. Delist review queue (Sheets)
      sheetsNode(
        "8",
        "Log delist review queue (Sheets)",
        1720,
        "DelistReview",
        '[["{worst_severity}","{stale_count}","{critical_count}","{warning_count}","{healthy_count}","{priority_actions}","{audit_summary}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // critical -> report email
      e("5", "7", "output_2"), // non-critical -> slack summary only
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 529. STANDARD BROWSE ABANDONMENT EMAIL (7 nodes)
  // ===================================================================

  "Standard Browse Abandonment Email": {
    summary:
      "Re-engages shoppers who browsed products without adding to cart. A browse session end webhook triggers product ranking via runCode, generates AI-powered related recommendations, checks browse depth to determine messaging intensity, sends a personalized browse recovery email, applies send-timing optimization, and logs performance to Sheets.",
    nodes: [
      trigger("Webhook: Browse session ended without cart", "webhook_app", {
        source: "Website Analytics",
        event: "browse.session.ended",
        howItStarts:
          "A visitor browses products for 3+ minutes but leaves without adding anything to cart, triggering re-engagement",
        setup:
          "Configure website tracking to fire a webhook when a browsing session ends without cart activity. Include viewed product IDs, session duration, and visitor email (if known).",
        inputFields: [],
      }),

      // 2. Product ranking (runCode)
      n("2", "runCode", "Rank browsed products by engagement", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse webhook and rank products by engagement signals
const payload = JSON.parse(vars.webhook_payload || "{}");
const products = payload.viewed_products || payload.products || [];
const email = payload.email || payload.visitor_email || payload.contact_email || "";
const sessionMinutes = Math.round((payload.session_duration || payload.duration || 0) / 60);
const ranked = products
  .map((p, i) => ({ ...p, score: (p.time_on_page || 30) + (p.revisits || 0) * 20 - i * 5 }))
  .sort((a, b) => b.score - a.score);
const top3 = ranked.slice(0, 3);
return {
  visitor_email: email,
  session_duration_min: sessionMinutes,
  products_viewed: products.length,
  top_product_title: top3[0] ? top3[0].title || top3[0].name || "Product" : "Product",
  top_product_url: top3[0] ? top3[0].url || top3[0].product_url || "" : "",
  top_product_image: top3[0] ? top3[0].image_url || top3[0].image || "" : "",
  top_product_price: top3[0] ? top3[0].price || "0" : "0",
  second_product_title: top3[1] ? top3[1].title || top3[1].name || "" : "",
  third_product_title: top3[2] ? top3[2].title || top3[2].name || "" : "",
  browse_depth: products.length >= 5 ? "deep" : products.length >= 2 ? "moderate" : "shallow"
};`,
          outputVariables: [
            { name: "visitor_email", type: "string" },
            { name: "session_duration_min", type: "number" },
            { name: "products_viewed", type: "number" },
            { name: "top_product_title", type: "string" },
            { name: "top_product_url", type: "string" },
            { name: "top_product_image", type: "string" },
            { name: "top_product_price", type: "string" },
            { name: "second_product_title", type: "string" },
            { name: "third_product_title", type: "string" },
            { name: "browse_depth", type: "string" },
          ],
        },
      }),

      // 3. AI related recommendations
      aiNode(
        "3",
        "AI related recommendations",
        520,
        "You are a product recommendation engine. Given the top browsed products, generate 3 related product recommendations with compelling descriptions. Focus on complementary items or popular alternatives. Respond JSON: {recommendations: [{title: string, reason: string, cta: string}], personalized_subject: string, opening_line: string}",
        "Top product: {top_product_title} (${top_product_price}). Also viewed: {second_product_title}, {third_product_title}. Session: {session_duration_min} minutes, {products_viewed} products viewed. Browse depth: {browse_depth}.",
        [
          {
            field: "recommendations",
            as: "ai_recommendations",
            type: "string",
          },
          {
            field: "personalized_subject",
            as: "email_subject",
            type: "string",
          },
          { field: "opening_line", as: "email_opening", type: "string" },
        ],
      ),

      // 4. Browse depth condition
      n("4", "condition", "Deep browse session?", 760, {
        conditionData: [
          {
            variable: "browse_depth",
            value: "deep",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
          {
            variable: "browse_depth",
            value: "moderate",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
        ],
      }),

      // 5. Browse recovery email
      emailNode(
        "5",
        "Browse recovery email",
        1000,
        "{visitor_email}",
        "{email_subject}",
        "{email_opening}\\n\\nWe noticed you were interested in {top_product_title} — and we think you have great taste.\\n\\nYour Top Pick:\\n{top_product_title} - ${top_product_price}\\n{top_product_url}\\n\\nYou Might Also Like:\\n{ai_recommendations}\\n\\nCome back and continue exploring — your next favorite find might be one click away.",
      ),

      // 6. Send timing delay
      n("6", "delayAction", "Optimal send timing delay", 1240, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 2, unit: "hours" },
        },
      }),

      // 7. Performance logging (Sheets)
      sheetsNode(
        "7",
        "Log browse recovery performance (Sheets)",
        1480,
        "BrowseRecovery",
        '[["{visitor_email}","{products_viewed}","{session_duration_min}","{browse_depth}","{top_product_title}","{top_product_price}","{email_subject}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // deep/moderate -> recovery email
      e("4", "7", "output_2"), // shallow -> log only, no email
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 530. STANDARD BROWSE ABANDONMENT SMS (6 nodes)
  // ===================================================================

  "Standard Browse Abandonment SMS": {
    summary:
      "SMS-based re-engagement for browse abandoners who have opted into SMS. A browse abandonment event triggers top product selection, generates a tracked short link via runCode, checks stock status, sends a personalized SMS via Twilio, and logs conversion data to Sheets.",
    nodes: [
      trigger("Webhook: Browse abandon (SMS-consented)", "webhook_app", {
        source: "Website Analytics",
        event: "browse.abandon.sms_eligible",
        howItStarts:
          "A visitor with SMS consent browses products for 3+ minutes and leaves without adding to cart, triggering SMS re-engagement",
        setup:
          "Configure browse tracking to fire webhook for SMS-consented visitors who abandon browse sessions. Include viewed products, visitor phone, and consent timestamp.",
        inputFields: [],
      }),

      // 2. Top product selection (runCode)
      n("2", "runCode", "Select top product and extract contact", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract top product and contact from webhook
const payload = JSON.parse(vars.webhook_payload || "{}");
const products = payload.viewed_products || payload.products || [];
const phone = payload.phone || payload.mobile || payload.sms_number || "";
const top = products.sort((a, b) => (b.time_on_page || 0) - (a.time_on_page || 0))[0] || {};
return {
  customer_phone: phone,
  top_product_id: top.id || top.product_id || "",
  top_product_title: top.title || top.name || "an item you viewed",
  top_product_price: top.price || "",
  top_product_url: top.url || top.product_url || "",
  stock_status: top.in_stock !== undefined ? (top.in_stock ? "in_stock" : "low_stock") : "unknown",
  products_viewed_count: String(products.length)
};`,
          outputVariables: [
            { name: "customer_phone", type: "string" },
            { name: "top_product_id", type: "string" },
            { name: "top_product_title", type: "string" },
            { name: "top_product_price", type: "string" },
            { name: "top_product_url", type: "string" },
            { name: "stock_status", type: "string" },
            { name: "products_viewed_count", type: "string" },
          ],
        },
      }),

      // 3. Generate tracked short link (runCode)
      n("3", "runCode", "Generate tracked short link", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build tracked short link with UTM parameters
const baseUrl = vars.top_product_url || "";
const separator = baseUrl.includes("?") ? "&" : "?";
const trackedUrl = baseUrl + separator + "utm_source=sms&utm_medium=browse_recovery&utm_campaign=browse_abandon&utm_content=" + vars.top_product_id;
return {
  tracked_link: trackedUrl,
  short_link_display: baseUrl.split("/").pop() || "product"
};`,
          outputVariables: [
            { name: "tracked_link", type: "string" },
            { name: "short_link_display", type: "string" },
          ],
        },
      }),

      // 4. Stock status condition
      n("4", "condition", "Product in stock?", 760, {
        conditionData: [
          {
            variable: "stock_status",
            value: "in_stock",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Browse SMS (Twilio)
      smsNode(
        "5",
        "Browse recovery SMS",
        1000,
        "{customer_phone}",
        "Still thinking about {top_product_title}? It is still available! Shop now: {tracked_link}",
      ),

      // 6. Conversion logging (Sheets)
      sheetsNode(
        "6",
        "Log SMS conversion data (Sheets)",
        1240,
        "BrowseSMS",
        '[["{customer_phone}","{top_product_title}","{top_product_price}","{stock_status}","{products_viewed_count}","{tracked_link}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // in stock -> send SMS
      e("4", "6", "output_2"), // out of stock -> log only
      e("5", "6"),
    ],
  },

  // ===================================================================
  // 531. STANDARD CART ABANDONMENT EMAIL SERIES (9 nodes)
  // ===================================================================

  "Standard Cart Abandonment Email Series": {
    summary:
      "Three-touch email series for cart abandoners with progressive urgency. A cart abandonment event triggers suppression checking, sends reminder email #1, waits and checks for purchase, sends social-proof email #2, waits again, sends urgency/incentive email #3, alerts on cart ops failures, and logs touch attribution to Sheets.",
    nodes: [
      trigger("Webhook: Cart abandoned", "webhook_app", {
        source: "E-Commerce Platform",
        event: "cart.abandoned",
        howItStarts:
          "A customer adds items to cart but does not complete checkout within 60 minutes, triggering a 3-email recovery series",
        setup:
          "Configure your e-commerce platform to fire a cart abandonment webhook after 60 minutes of inactivity. Include cart contents, customer email, cart value, and cart recovery URL.",
        inputFields: [],
      }),

      // 2. Suppression check (runCode)
      n("2", "runCode", "Suppression and cart extraction", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse cart data and check suppression rules
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = payload.email || payload.customer_email || payload.contact_email || "";
const cartItems = payload.items || payload.cart_items || payload.line_items || [];
const cartValue = payload.total || payload.cart_total || cartItems.reduce((s, i) => s + (parseFloat(i.price || 0) * (i.quantity || 1)), 0);
const recoveryUrl = payload.recovery_url || payload.checkout_url || payload.cart_url || "";
const itemNames = cartItems.map(i => i.title || i.name || i.product_name).filter(Boolean).join(", ");
const itemCount = cartItems.length;
const suppressed = !email || cartValue < 1 ? "yes" : "no";
return {
  customer_email: email,
  cart_value: String(cartValue.toFixed(2)),
  cart_items_summary: itemNames || "your selected items",
  cart_item_count: String(itemCount),
  recovery_url: recoveryUrl,
  suppressed: suppressed,
  first_item_name: cartItems[0] ? (cartItems[0].title || cartItems[0].name || "your item") : "your item",
  first_item_image: cartItems[0] ? (cartItems[0].image_url || cartItems[0].image || "") : ""
};`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "cart_value", type: "string" },
            { name: "cart_items_summary", type: "string" },
            { name: "cart_item_count", type: "string" },
            { name: "recovery_url", type: "string" },
            { name: "suppressed", type: "string" },
            { name: "first_item_name", type: "string" },
            { name: "first_item_image", type: "string" },
          ],
        },
      }),

      // 3. Email 1 — Friendly reminder
      emailNode(
        "3",
        "Email 1: Friendly cart reminder",
        520,
        "{customer_email}",
        "You left something behind — {first_item_name}",
        "Hi there,\\n\\nLooks like you left {cart_items_summary} in your cart (${cart_value}).\\n\\nYour cart is saved and ready for you:\\n{recovery_url}\\n\\nNeed help? Reply to this email and our team will assist you.",
      ),

      // 4. Purchase check delay #1
      n("4", "delayAction", "Wait 24 hours for purchase", 760, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 24, unit: "hours" },
        },
      }),

      // 5. Email 2 — Social proof
      emailNode(
        "5",
        "Email 2: Social proof nudge",
        1000,
        "{customer_email}",
        "Others love {first_item_name} — still in your cart",
        "Hi there,\\n\\nJust a heads up — {first_item_name} is a customer favorite and sells fast.\\n\\nYour cart (${cart_value}):\\n{cart_items_summary}\\n\\nDon't miss out — complete your order now:\\n{recovery_url}\\n\\nFree shipping and easy returns on all orders.",
      ),

      // 6. Purchase check delay #2
      n("6", "delayAction", "Wait 48 hours for purchase", 1240, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 48, unit: "hours" },
        },
      }),

      // 7. Email 3 — Urgency + incentive
      emailNode(
        "7",
        "Email 3: Urgency + incentive",
        1480,
        "{customer_email}",
        "Last chance: 10% off your cart — {first_item_name}",
        "Hi there,\\n\\nYour cart is about to expire. We would hate for you to miss out on {cart_items_summary}.\\n\\nAs a special offer, use code COMEBACK10 for 10% off your order of ${cart_value}.\\n\\nComplete your purchase now:\\n{recovery_url}?discount=COMEBACK10\\n\\nThis offer expires in 24 hours.",
      ),

      // 8. Cart ops failure alert
      slackNode(
        "8",
        "Slack: Cart recovery ops alert",
        1720,
        ":warning: Cart Recovery Series Complete (No Conversion)\\nEmail: {customer_email}\\nCart Value: ${cart_value}\\nItems: {cart_items_summary} ({cart_item_count} items)\\n3-touch series exhausted. Consider manual outreach for high-value carts.",
        400,
      ),

      // 9. Touch attribution logging (Sheets)
      sheetsNode(
        "9",
        "Log touch attribution (Sheets)",
        1960,
        "CartRecovery",
        '[["{customer_email}","{cart_value}","{cart_item_count}","{cart_items_summary}","{first_item_name}","3-touch-complete"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 532. STANDARD CART ABANDONMENT SMS (7 nodes)
  // ===================================================================

  "Standard Cart Abandonment SMS": {
    summary:
      "Two-touch SMS cart recovery for SMS-consented customers. A cart abandonment event extracts cart summary and restore link, sends an immediate SMS reminder via Twilio, waits and checks for purchase, sends an urgency SMS, monitors opt-out responses, and logs performance to Sheets.",
    nodes: [
      trigger("Webhook: Cart abandon (SMS-consented)", "webhook_app", {
        source: "E-Commerce Platform",
        event: "cart.abandoned.sms_eligible",
        howItStarts:
          "An SMS-consented customer abandons their cart, triggering a 2-SMS recovery series with tracked restore links",
        setup:
          "Configure cart abandonment webhook for SMS-consented customers. Include cart contents, customer phone, cart value, and restore link.",
        inputFields: [],
      }),

      // 2. Cart summary + restore link (runCode)
      n("2", "runCode", "Extract cart and build restore link", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse cart data and build tracked restore link
const payload = JSON.parse(vars.webhook_payload || "{}");
const phone = payload.phone || payload.mobile || payload.sms_number || "";
const cartItems = payload.items || payload.cart_items || payload.line_items || [];
const cartValue = payload.total || payload.cart_total || 0;
const restoreUrl = payload.recovery_url || payload.checkout_url || "";
const trackedUrl = restoreUrl + (restoreUrl.includes("?") ? "&" : "?") + "utm_source=sms&utm_medium=cart_recovery";
const topItem = cartItems[0] ? (cartItems[0].title || cartItems[0].name || "your item") : "your item";
return {
  customer_phone: phone,
  cart_value: String(parseFloat(cartValue).toFixed(2)),
  cart_item_count: String(cartItems.length),
  top_item_name: topItem,
  restore_link: trackedUrl,
  customer_email: payload.email || payload.customer_email || ""
};`,
          outputVariables: [
            { name: "customer_phone", type: "string" },
            { name: "cart_value", type: "string" },
            { name: "cart_item_count", type: "string" },
            { name: "top_item_name", type: "string" },
            { name: "restore_link", type: "string" },
            { name: "customer_email", type: "string" },
          ],
        },
      }),

      // 3. SMS 1 — Friendly reminder
      smsNode(
        "3",
        "SMS 1: Cart reminder",
        520,
        "{customer_phone}",
        "Hey! You left {top_item_name} in your cart (${cart_value}). Your cart is saved — complete your order here: {restore_link}",
      ),

      // 4. Purchase check delay
      n("4", "delayAction", "Wait 24 hours for purchase", 760, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 24, unit: "hours" },
        },
      }),

      // 5. SMS 2 — Urgency
      smsNode(
        "5",
        "SMS 2: Urgency reminder",
        1000,
        "{customer_phone}",
        "Last chance! Your cart with {top_item_name} (${cart_value}) expires soon. Grab it before it is gone: {restore_link}",
      ),

      // 6. Opt-out monitoring (runCode)
      n("6", "runCode", "Check opt-out status", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Monitor opt-out status post-send
const optedOut = vars.sms_delivery_status === "unsubscribed" || vars.sms_delivery_status === "opted_out";
return {
  opted_out: optedOut ? "yes" : "no",
  final_status: optedOut ? "opted_out" : "series_complete"
};`,
          outputVariables: [
            { name: "opted_out", type: "string" },
            { name: "final_status", type: "string" },
          ],
        },
      }),

      // 7. Performance logging (Sheets)
      sheetsNode(
        "7",
        "Log SMS cart recovery (Sheets)",
        1480,
        "CartSMS",
        '[["{customer_phone}","{cart_value}","{cart_item_count}","{top_item_name}","{final_status}","{restore_link}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 533. STANDARD REPLENISHMENT REMINDER (6 nodes)
  // ===================================================================

  "Standard Replenishment Reminder": {
    summary:
      "Timely reorder reminders for consumable products based on estimated usage intervals. A consumable delivery event triggers interval calculation via runCode, delays until the estimated reorder window, checks if the customer has already reordered, sends a replenishment reminder email, and alerts ops on failures.",
    nodes: [
      trigger("Webhook: Consumable product delivered", "webhook_app", {
        source: "E-Commerce Platform",
        event: "order.fulfilled",
        howItStarts:
          "A consumable product is delivered to the customer, starting the countdown to a replenishment reminder based on estimated product usage interval",
        setup:
          "Configure fulfillment webhook. Maintain a product-to-replenishment-interval map (e.g., 30-day supply vitamins = 25 days). Include order details and customer email.",
        inputFields: [],
      }),

      // 2. Interval calculation (runCode)
      n("2", "runCode", "Calculate replenishment interval", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate reorder timing based on product category
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = payload.email || payload.customer_email || "";
const items = payload.items || payload.line_items || [];
const product = items[0] || {};
const productName = product.title || product.name || "your product";
const productId = product.id || product.product_id || "";
const category = (product.category || product.product_type || "").toLowerCase();
// Default intervals by category (days)
const intervals = { "vitamins": 25, "supplements": 25, "skincare": 28, "coffee": 14, "pet_food": 21, "cleaning": 30, "baby": 14, "personal_care": 28 };
const matchedCategory = Object.keys(intervals).find(k => category.includes(k));
const intervalDays = matchedCategory ? intervals[matchedCategory] : 25;
const quantity = product.quantity || 1;
const adjustedInterval = intervalDays * quantity;
return {
  customer_email: email,
  product_name: productName,
  product_id: productId,
  replenish_interval_days: String(adjustedInterval),
  product_category: matchedCategory || "general",
  reorder_url: product.url || product.product_url || "",
  order_id: payload.order_id || payload.id || ""
};`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "product_name", type: "string" },
            { name: "product_id", type: "string" },
            { name: "replenish_interval_days", type: "string" },
            { name: "product_category", type: "string" },
            { name: "reorder_url", type: "string" },
            { name: "order_id", type: "string" },
          ],
        },
      }),

      // 3. Scheduled delay until reorder window
      n("3", "delayAction", "Wait for replenishment window", 520, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 25, unit: "days" },
        },
      }),

      // 4. Reorder check condition
      n("4", "condition", "Customer has not reordered?", 760, {
        conditionData: [
          {
            variable: "customer_email",
            value: "",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Replenishment email
      emailNode(
        "5",
        "Replenishment reminder email",
        1000,
        "{customer_email}",
        "Time to restock — {product_name}",
        "Hi there,\\n\\nIt has been about {replenish_interval_days} days since your last order of {product_name}. Based on typical usage, you might be running low!\\n\\nReorder now to avoid running out:\\n{reorder_url}\\n\\nSet up auto-replenishment and never worry about running out again.\\n\\nOrder Reference: {order_id}",
      ),

      // 6. Ops failure alert
      slackNode(
        "6",
        "Slack: Replenishment ops alert",
        1240,
        ":pill: Replenishment Reminder Sent\\nCustomer: {customer_email}\\nProduct: {product_name} ({product_category})\\nInterval: {replenish_interval_days} days\\nOrder: {order_id}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // hasn't reordered -> send reminder
      e("4", "6", "output_2"), // already reordered or invalid -> ops alert
      e("5", "6"),
    ],
  },

  // ===================================================================
  // 534. STORE REVIEW REQUEST AFTER STORE PICKUP (7 nodes)
  // ===================================================================

  "Store Review Request after Store Pickup": {
    summary:
      "Solicits store reviews from customers who completed in-store pickup. A pickup complete trigger pulls order/store details, waits a post-pickup period, checks for recent review suppression, sends a store-specific review request email, alerts store ops on failures via Slack, and logs at the store level to Sheets.",
    nodes: [
      trigger("Webhook: Store pickup completed", "webhook_app", {
        source: "POS / BOPIS System",
        event: "pickup.completed",
        howItStarts:
          "A customer picks up their order in-store, triggering a delayed review request specific to the store location",
        setup:
          "Configure POS or BOPIS system to fire a webhook when a customer picks up an order. Include order ID, customer email, store ID, store name, and pickup timestamp.",
        inputFields: [],
      }),

      // 2. Order/store details pull (runCode)
      n("2", "runCode", "Extract order and store details", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse pickup event for order and store details
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = payload.email || payload.customer_email || "";
const customerName = payload.customer_name || payload.name || "Valued Customer";
const orderId = payload.order_id || payload.id || "";
const storeId = payload.store_id || payload.location_id || "";
const storeName = payload.store_name || payload.location_name || "our store";
const storeAddress = payload.store_address || payload.address || "";
const pickupDate = payload.pickup_date || payload.completed_at || new Date().toISOString().split("T")[0];
const itemCount = (payload.items || payload.line_items || []).length;
const reviewUrl = payload.review_url || "https://{store_domain}/review/" + storeId;
return {
  customer_email: email,
  customer_name: customerName,
  order_id: orderId,
  store_id: storeId,
  store_name: storeName,
  store_address: storeAddress,
  pickup_date: pickupDate,
  item_count: String(itemCount),
  review_url: reviewUrl,
  has_recent_review: "no"
};`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "order_id", type: "string" },
            { name: "store_id", type: "string" },
            { name: "store_name", type: "string" },
            { name: "store_address", type: "string" },
            { name: "pickup_date", type: "string" },
            { name: "item_count", type: "string" },
            { name: "review_url", type: "string" },
            { name: "has_recent_review", type: "string" },
          ],
        },
      }),

      // 3. Post-pickup delay
      n("3", "delayAction", "Wait 2 days post-pickup", 520, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 2, unit: "days" },
        },
      }),

      // 4. Recent review suppression check condition
      n("4", "condition", "No recent review from customer?", 760, {
        conditionData: [
          {
            variable: "has_recent_review",
            value: "no",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Store-specific review email
      emailNode(
        "5",
        "Store review request email",
        1000,
        "{customer_email}",
        "How was your pickup at {store_name}?",
        "Hi {customer_name},\\n\\nThank you for picking up your order (#{order_id}) at {store_name} on {pickup_date}.\\n\\nWe would love to hear about your in-store pickup experience! Your feedback helps us improve the service at {store_name} ({store_address}).\\n\\nLeave a quick review here:\\n{review_url}\\n\\nIt only takes a minute and makes a real difference for our store team. Thank you!",
      ),

      // 6. Store ops failure alert (Slack)
      slackNode(
        "6",
        "Slack: Store review ops alert",
        1240,
        ":department_store: Store Review Request Sent\\nStore: {store_name} (ID: {store_id})\\nCustomer: {customer_email}\\nOrder: {order_id}\\nPickup Date: {pickup_date}\\nItems: {item_count}",
        400,
      ),

      // 7. Store-level logging (Sheets)
      sheetsNode(
        "7",
        "Log store review request (Sheets)",
        1480,
        "StoreReviews",
        '[["{store_id}","{store_name}","{customer_email}","{order_id}","{pickup_date}","{item_count}","{review_url}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // no recent review -> send request
      e("4", "6", "output_2"), // has recent review -> alert only
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 535. STORE-PICKUP RECOVERY (7 nodes)
  // ===================================================================

  "Store-Pickup Recovery": {
    summary:
      "Recovers abandoned BOPIS (Buy Online, Pick Up In Store) checkouts. A BOPIS checkout abandonment triggers cart and store data pull from Shopify, checks local inventory, routes on availability, sends a 'ready today' recovery email, offers alternate store messaging, and logs BOPIS recovery data to Sheets.",
    nodes: [
      trigger("Webhook: BOPIS checkout abandoned", "webhook_app", {
        source: "E-Commerce Platform",
        event: "checkout.abandoned.bopis",
        howItStarts:
          "A customer starts a buy-online-pick-up-in-store checkout but abandons before completing, triggering a recovery flow with local inventory messaging",
        setup:
          "Configure your e-commerce platform to detect BOPIS checkout abandonments. Include cart items, selected store location, customer email, and cart recovery URL.",
        inputFields: [],
      }),

      // 2. Cart + store pull (Shopify)
      n("2", "runCode", "Extract BOPIS cart and store details", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse BOPIS abandonment data
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = payload.email || payload.customer_email || "";
const storeId = payload.store_id || payload.pickup_location_id || "";
const storeName = payload.store_name || payload.pickup_location_name || "your selected store";
const storeAddress = payload.store_address || "";
const cartItems = payload.items || payload.cart_items || payload.line_items || [];
const cartValue = payload.total || payload.cart_total || 0;
const recoveryUrl = payload.recovery_url || payload.checkout_url || "";
const topItem = cartItems[0] || {};
return {
  customer_email: email,
  store_id: storeId,
  store_name: storeName,
  store_address: storeAddress,
  cart_value: String(parseFloat(cartValue).toFixed(2)),
  cart_item_count: String(cartItems.length),
  top_item_name: topItem.title || topItem.name || "your item",
  top_item_id: topItem.id || topItem.product_id || "",
  recovery_url: recoveryUrl,
  items_summary: cartItems.map(i => i.title || i.name).filter(Boolean).join(", ") || "your selected items"
};`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "store_id", type: "string" },
            { name: "store_name", type: "string" },
            { name: "store_address", type: "string" },
            { name: "cart_value", type: "string" },
            { name: "cart_item_count", type: "string" },
            { name: "top_item_name", type: "string" },
            { name: "top_item_id", type: "string" },
            { name: "recovery_url", type: "string" },
            { name: "items_summary", type: "string" },
          ],
        },
      }),

      // 3. Local inventory check
      shopifyNode(
        "3",
        "Check local inventory (Shopify)",
        520,
        "GET",
        "inventory_levels.json?location_ids={store_id}&inventory_item_ids={top_item_id}",
        [],
        [
          {
            name: "inventory_levels[0].available",
            customName: "local_stock_available",
            type: "number",
          },
          {
            name: "inventory_levels.length",
            customName: "inventory_levels_count",
            type: "number",
          },
        ],
      ),

      // 4. Availability condition
      n("4", "condition", "Item available at selected store?", 760, {
        conditionData: [
          {
            variable: "local_stock_available",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. "Ready today" recovery email (True path)
      emailNode(
        "5",
        "Ready Today recovery email",
        1000,
        "{customer_email}",
        "{top_item_name} is ready for pickup today at {store_name}",
        "Great news!\\n\\n{items_summary} is in stock and ready for same-day pickup at {store_name} ({store_address}).\\n\\nYour Cart (${cart_value}):\\n{items_summary}\\n\\nComplete your BOPIS order now and pick up today:\\n{recovery_url}\\n\\nNo shipping wait — grab it on your schedule.",
      ),

      // 6. Alternate-store messaging (False path)
      emailNode(
        "6",
        "Alternate store availability email",
        1000,
        "{customer_email}",
        "{top_item_name} — available at a nearby store",
        "Hi there,\\n\\n{top_item_name} is temporarily out of stock at {store_name}, but it may be available at a nearby location.\\n\\nYour Cart (${cart_value}):\\n{items_summary}\\n\\nCheck availability at other stores or switch to home delivery:\\n{recovery_url}\\n\\nWe want to make sure you get what you need!",
        400,
      ),

      // 7. BOPIS recovery logging (Sheets)
      sheetsNode(
        "7",
        "Log BOPIS recovery (Sheets)",
        1240,
        "BOPISRecovery",
        '[["{customer_email}","{store_name}","{store_id}","{cart_value}","{cart_item_count}","{top_item_name}","{local_stock_available}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // in stock -> ready today email
      e("4", "6", "output_2"), // out of stock -> alternate store email
      e("5", "7"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 536. STRIPE CUSTOMER + PAYMENT FLOW (8 nodes)
  // ===================================================================

  "Stripe Customer + Payment Flow": {
    summary:
      "End-to-end billable customer onboarding with Stripe payment collection. A billable customer trigger creates a Stripe customer, generates a payment link via runCode, emails the payment link with due date, listens for the Stripe completion webhook, updates internal records, notifies finance via Slack, and escalates non-payment with reminders.",
    nodes: [
      trigger("Webhook: New billable customer", "webhook_app", {
        source: "CRM / Billing System",
        event: "customer.billable.created",
        howItStarts:
          "A new customer becomes billable (contract signed, trial ended, or invoice generated), triggering Stripe customer creation and payment collection",
        setup:
          "Configure your CRM or billing system to fire a webhook when a customer becomes billable. Include customer name, email, billing amount, currency, and due date.",
        inputFields: [],
      }),

      // 2. Stripe customer creation
      n("2", "runCode", "Extract billing details", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse billing customer payload
const payload = JSON.parse(vars.webhook_payload || "{}");
return {
  customer_email: payload.email || payload.customer_email || "",
  customer_name: payload.name || payload.customer_name || payload.company_name || "",
  billing_amount: String(payload.amount || payload.billing_amount || 0),
  billing_amount_cents: String(Math.round((payload.amount || payload.billing_amount || 0) * 100)),
  currency: payload.currency || "usd",
  due_date: payload.due_date || payload.payment_due || "",
  invoice_id: payload.invoice_id || payload.id || "",
  description: payload.description || payload.plan_name || "Service payment"
};`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "billing_amount", type: "string" },
            { name: "billing_amount_cents", type: "string" },
            { name: "currency", type: "string" },
            { name: "due_date", type: "string" },
            { name: "invoice_id", type: "string" },
            { name: "description", type: "string" },
          ],
        },
      }),

      // 3. Create Stripe customer
      stripeNode(
        "3",
        "Create Stripe customer",
        520,
        "POST",
        "customers",
        [
          { key: "email", value: "{customer_email}" },
          { key: "name", value: "{customer_name}" },
          { key: "metadata[invoice_id]", value: "{invoice_id}" },
        ],
        [
          { name: "id", customName: "stripe_customer_id", type: "string" },
          { name: "created", customName: "stripe_created_at", type: "number" },
        ],
      ),

      // 4. Payment link generation (runCode)
      n("4", "runCode", "Generate payment link", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build Stripe payment link
const paymentUrl = "https://checkout.stripe.com/pay/" + vars.stripe_customer_id + "?amount=" + vars.billing_amount_cents + "&currency=" + vars.currency + "&description=" + encodeURIComponent(vars.description);
const dueDateFormatted = vars.due_date || "within 7 days";
return {
  payment_url: paymentUrl,
  due_date_formatted: dueDateFormatted,
  payment_status: "pending"
};`,
          outputVariables: [
            { name: "payment_url", type: "string" },
            { name: "due_date_formatted", type: "string" },
            { name: "payment_status", type: "string" },
          ],
        },
      }),

      // 5. Payment email with due date
      emailNode(
        "5",
        "Payment request email",
        1000,
        "{customer_email}",
        "Payment Due: ${billing_amount} for {description} — Due {due_date_formatted}",
        "Hi {customer_name},\\n\\nYour invoice for {description} is ready.\\n\\nAmount Due: ${billing_amount} {currency}\\nDue Date: {due_date_formatted}\\nInvoice: {invoice_id}\\n\\nPay securely online:\\n{payment_url}\\n\\nIf you have any questions about this invoice, please reply to this email.\\n\\nThank you for your business!",
      ),

      // 6. Stripe completion webhook check (condition)
      n("6", "condition", "Payment completed?", 1240, {
        conditionData: [
          {
            variable: "payment_status",
            value: "pending",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Finance Slack notification (True/pending path — needs follow-up)
      slackNode(
        "7",
        "Slack: Finance payment notification",
        1480,
        ":credit_card: Payment Request Sent\\nCustomer: {customer_name} ({customer_email})\\nAmount: ${billing_amount} {currency}\\nDue: {due_date_formatted}\\nInvoice: {invoice_id}\\nStripe Customer: {stripe_customer_id}\\nStatus: {payment_status}",
      ),

      // 8. Reminder escalation for non-payment
      emailNode(
        "8",
        "Payment reminder escalation",
        1720,
        "{customer_email}",
        "Reminder: Payment of ${billing_amount} overdue — {description}",
        "Hi {customer_name},\\n\\nThis is a friendly reminder that your payment of ${billing_amount} {currency} for {description} is now overdue.\\n\\nOriginal Due Date: {due_date_formatted}\\nInvoice: {invoice_id}\\n\\nPlease complete your payment as soon as possible:\\n{payment_url}\\n\\nIf you have already made this payment, please disregard this notice. For questions, reply to this email.",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // still pending -> finance notification + escalation
      e("6", "7", "output_2"), // paid -> finance notification only
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 537. SUBSCRIPTION BOX CUSTOMIZER BOT (9 nodes)
  // ===================================================================

  "Subscription Box Customizer Bot": {
    summary:
      "Chat-driven subscription box customization with real-time swap validation. A chat customization trigger pulls subscription state, retrieves allowed substitutes, interprets swap requests via AI, checks the customization deadline, previews the box with price diffs, applies changes on confirmation, sends a confirmation email, and alerts ops on failures via Slack.",
    nodes: [
      trigger("Webhook: Chat customization request", "webhook_app", {
        source: "Chat Widget",
        event: "chat.subscription.customize",
        howItStarts:
          "A subscriber initiates a box customization request through the chat widget, requesting item swaps or additions for their upcoming box",
        setup:
          "Configure chat platform to fire a webhook when a subscriber requests box customization. Include subscriber ID, current box items, and swap request text.",
        inputFields: [],
      }),

      // 2. Subscription state pull
      n("2", "hitApi", "Pull subscription state", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{subscription_api}/api/v1/subscriptions/{subscriber_id}",
          headers: [
            { key: "Authorization", value: "Bearer {subscription_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "subscription.id",
              customName: "subscription_id",
              type: "string",
            },
            {
              name: "subscription.status",
              customName: "sub_status",
              type: "string",
            },
            {
              name: "subscription.next_billing_date",
              customName: "next_billing",
              type: "string",
            },
            {
              name: "subscription.current_box.items",
              customName: "current_items",
              type: "string",
            },
            {
              name: "subscription.customer.email",
              customName: "subscriber_email",
              type: "string",
            },
            {
              name: "subscription.customer.name",
              customName: "subscriber_name",
              type: "string",
            },
            {
              name: "subscription.plan.name",
              customName: "plan_name",
              type: "string",
            },
          ],
        },
      }),

      // 3. Allowed substitutes pull
      n("3", "hitApi", "Pull allowed substitutes catalog", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{subscription_api}/api/v1/catalog/substitutes?plan={plan_name}",
          headers: [
            { key: "Authorization", value: "Bearer {subscription_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "substitutes",
              customName: "available_substitutes",
              type: "string",
            },
            {
              name: "substitutes.length",
              customName: "substitute_count",
              type: "number",
            },
            {
              name: "customization_deadline",
              customName: "customization_deadline",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI swap interpretation
      aiNode(
        "4",
        "AI interpret swap requests",
        760,
        "You are a subscription box customization assistant. Given the subscriber current box items, available substitutes, and the customer swap request, interpret what items should be swapped. Match requested items to available substitutes. Calculate price differences. Respond JSON: {swaps: [{remove_item: string, add_item: string, price_diff: number}], total_price_diff: number, valid_swaps: number, invalid_requests: string, swap_summary: string, confirmation_message: string}",
        "Current box: {current_items}. Available substitutes: {available_substitutes}. Customer request: {webhook_payload}",
        [
          { field: "swaps", as: "proposed_swaps", type: "string" },
          { field: "total_price_diff", as: "total_price_diff", type: "number" },
          { field: "valid_swaps", as: "valid_swap_count", type: "number" },
          { field: "invalid_requests", as: "invalid_requests", type: "string" },
          { field: "swap_summary", as: "swap_summary", type: "string" },
          {
            field: "confirmation_message",
            as: "confirmation_message",
            type: "string",
          },
        ],
      ),

      // 5. Deadline check condition
      n("5", "condition", "Within customization deadline?", 1000, {
        conditionData: [
          {
            variable: "valid_swap_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Box preview with price diffs (sendMessage via chat)
      n("6", "sendMessage", "Send box preview with price diffs", 1240, {
        sendMessageDetails: {
          content:
            "Here is your customized box preview:\\n\\n{swap_summary}\\n\\nPrice adjustment: ${total_price_diff}\\n\\n{confirmation_message}\\n\\nReply CONFIRM to apply these changes or CANCEL to keep your current box.",
        },
      }),

      // 7. Apply changes on confirmation
      n("7", "hitApi", "Apply box customizations", 1480, {
        hitApiDetails: {
          method: "PUT",
          url: "https://{subscription_api}/api/v1/subscriptions/{subscription_id}/customize",
          headers: [
            { key: "Authorization", value: "Bearer {subscription_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "swaps", value: "{proposed_swaps}" },
            { key: "price_adjustment", value: "{total_price_diff}" },
          ],
          variables: [
            {
              name: "updated_box.items",
              customName: "updated_items",
              type: "string",
            },
            {
              name: "updated_box.total",
              customName: "updated_total",
              type: "string",
            },
            {
              name: "status",
              customName: "customization_status",
              type: "string",
            },
          ],
        },
      }),

      // 8. Confirmation email
      emailNode(
        "8",
        "Confirmation email",
        1720,
        "{subscriber_email}",
        "Your {plan_name} box has been customized!",
        "Hi {subscriber_name},\\n\\nYour subscription box has been updated with the following changes:\\n\\n{swap_summary}\\n\\nPrice adjustment: ${total_price_diff}\\nUpdated total: ${updated_total}\\n\\nYour updated box will ship before your next billing date ({next_billing}).\\n\\nEnjoy your customized box!",
      ),

      // 9. Ops failure alert (Slack)
      slackNode(
        "9",
        "Slack: Customization ops alert",
        1960,
        ":package: Box Customization Applied\\nSubscriber: {subscriber_email} ({subscription_id})\\nPlan: {plan_name}\\nSwaps: {valid_swap_count} | Price Diff: ${total_price_diff}\\nStatus: {customization_status}\\nInvalid Requests: {invalid_requests}",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // valid swaps -> show preview
      e("5", "9", "output_2"), // no valid swaps -> ops alert
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 538. SUBSCRIPTION CHECKOUT ABANDONMENT (8 nodes)
  // ===================================================================

  "Subscription Checkout Abandonment": {
    summary:
      "Recovers abandoned subscription checkout with AI-personalized messaging. A subscription checkout abandon event pulls plan/trial details, uses AI to infer hesitation reasons, routes on trial availability, sends flexibility or savings messaging, delivers the recovery email, and logs subscription growth data to Sheets.",
    nodes: [
      trigger("Webhook: Subscription checkout abandoned", "webhook_app", {
        source: "Subscription Platform",
        event: "checkout.subscription.abandoned",
        howItStarts:
          "A customer starts a subscription checkout but abandons before completing payment, triggering an AI-personalized recovery flow",
        setup:
          "Configure subscription platform to detect checkout abandonments. Include plan details, trial info, customer email, and checkout recovery URL.",
        inputFields: [],
      }),

      // 2. Plan/trial details pull (runCode)
      n("2", "runCode", "Extract plan and trial details", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse subscription checkout abandonment data
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = payload.email || payload.customer_email || "";
const planName = payload.plan_name || payload.plan || "subscription";
const planPrice = payload.price || payload.plan_price || "0";
const billingCycle = payload.billing_cycle || payload.interval || "monthly";
const hasTrial = payload.trial_days && parseInt(payload.trial_days) > 0 ? "yes" : "no";
const trialDays = payload.trial_days || "0";
const recoveryUrl = payload.recovery_url || payload.checkout_url || "";
const features = payload.features || payload.plan_features || "";
return {
  customer_email: email,
  plan_name: planName,
  plan_price: String(planPrice),
  billing_cycle: billingCycle,
  has_trial: hasTrial,
  trial_days: trialDays,
  recovery_url: recoveryUrl,
  plan_features: features,
  annual_savings: billingCycle === "monthly" ? String((parseFloat(planPrice) * 12 * 0.17).toFixed(2)) : "0"
};`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "plan_name", type: "string" },
            { name: "plan_price", type: "string" },
            { name: "billing_cycle", type: "string" },
            { name: "has_trial", type: "string" },
            { name: "trial_days", type: "string" },
            { name: "recovery_url", type: "string" },
            { name: "plan_features", type: "string" },
            { name: "annual_savings", type: "string" },
          ],
        },
      }),

      // 3. AI hesitation inference
      aiNode(
        "3",
        "AI infer hesitation reasons",
        520,
        "You are a subscription conversion specialist. Based on the plan details and checkout context, infer the most likely reasons for hesitation and generate personalized recovery messaging. Consider price sensitivity, commitment concerns, feature uncertainty, or comparison shopping. Respond JSON: {primary_hesitation: string, recovery_angle: string, subject_line: string, opening_hook: string, social_proof: string, cta_text: string}",
        "Plan: {plan_name} at ${plan_price}/{billing_cycle}. Trial available: {has_trial} ({trial_days} days). Features: {plan_features}. Annual savings if switching to yearly: ${annual_savings}.",
        [
          {
            field: "primary_hesitation",
            as: "hesitation_reason",
            type: "string",
          },
          { field: "recovery_angle", as: "recovery_angle", type: "string" },
          { field: "subject_line", as: "recovery_subject", type: "string" },
          { field: "opening_hook", as: "opening_hook", type: "string" },
          { field: "social_proof", as: "social_proof", type: "string" },
          { field: "cta_text", as: "cta_text", type: "string" },
        ],
      ),

      // 4. Trial exists condition
      n("4", "condition", "Trial available?", 760, {
        conditionData: [
          {
            variable: "has_trial",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Flexibility messaging email (True — trial available)
      emailNode(
        "5",
        "Flexibility: Trial reminder email",
        1000,
        "{customer_email}",
        "{recovery_subject}",
        "{opening_hook}\\n\\nStill considering {plan_name}? Try it risk-free with a {trial_days}-day free trial — no commitment required.\\n\\n{social_proof}\\n\\nStart your free trial now:\\n{recovery_url}\\n\\nNo charge until your trial ends. Cancel anytime.",
      ),

      // 6. Savings messaging email (False — no trial)
      emailNode(
        "6",
        "Savings: Value proposition email",
        1000,
        "{customer_email}",
        "{recovery_subject}",
        "{opening_hook}\\n\\nWe noticed you were exploring {plan_name} (${plan_price}/{billing_cycle}).\\n\\n{social_proof}\\n\\nSwitch to annual billing and save ${annual_savings}/year.\\n\\nComplete your subscription:\\n{recovery_url}\\n\\n{cta_text}",
        400,
      ),

      // 7. Recovery send delay
      n("7", "delayAction", "Optimal recovery timing", 1240, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 4, unit: "hours" },
        },
      }),

      // 8. Subscription growth logging (Sheets)
      sheetsNode(
        "8",
        "Log subscription recovery (Sheets)",
        1480,
        "SubGrowth",
        '[["{customer_email}","{plan_name}","{plan_price}","{billing_cycle}","{has_trial}","{hesitation_reason}","{recovery_angle}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // trial available -> flexibility email
      e("4", "6", "output_2"), // no trial -> savings email
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 539. SUBSCRIPTION CONVERSION FROM ONE-TIME BUYER (8 nodes)
  // ===================================================================

  "Subscription Conversion From One-Time Buyer": {
    summary:
      "Converts one-time consumable buyers into subscribers by demonstrating savings. A one-time consumable purchase triggers subscription economics calculation via runCode, checks repeat purchase patterns, delays for product experience, sends a conversion email, tracks conversion, logs to Sheets, and alerts growth ops.",
    nodes: [
      trigger("Webhook: One-time consumable purchase", "webhook_app", {
        source: "E-Commerce Platform",
        event: "order.created",
        howItStarts:
          "A customer purchases a consumable product as a one-time order (not a subscription), triggering a conversion nurture to subscription",
        setup:
          "Configure order webhook for consumable product categories. Filter for non-subscription orders. Include product details, order value, and customer email.",
        inputFields: [],
      }),

      // 2. Subscription economics calc (runCode)
      n("2", "runCode", "Calculate subscription economics", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse order and calculate subscription value proposition
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = payload.email || payload.customer_email || "";
const customerName = payload.customer_name || payload.name || "";
const items = payload.items || payload.line_items || [];
const product = items[0] || {};
const productName = product.title || product.name || "";
const productPrice = parseFloat(product.price || 0);
const productId = product.id || product.product_id || "";
const isSubscription = payload.subscription || payload.is_subscription || false;
const subscriptionDiscount = 0.15; // 15% subscription savings
const subscriptionPrice = (productPrice * (1 - subscriptionDiscount)).toFixed(2);
const annualSavings = (productPrice * subscriptionDiscount * 12).toFixed(2);
const repeatOrders = parseInt(payload.customer_order_count || payload.order_count || "1");
return {
  customer_email: email,
  customer_name: customerName,
  product_name: productName,
  product_id: productId,
  one_time_price: String(productPrice.toFixed(2)),
  subscription_price: subscriptionPrice,
  annual_savings: annualSavings,
  discount_percent: "15",
  is_repeat_buyer: repeatOrders > 1 ? "yes" : "no",
  repeat_order_count: String(repeatOrders),
  order_id: payload.order_id || payload.id || "",
  product_url: product.url || product.product_url || ""
};`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "product_name", type: "string" },
            { name: "product_id", type: "string" },
            { name: "one_time_price", type: "string" },
            { name: "subscription_price", type: "string" },
            { name: "annual_savings", type: "string" },
            { name: "discount_percent", type: "string" },
            { name: "is_repeat_buyer", type: "string" },
            { name: "repeat_order_count", type: "string" },
            { name: "order_id", type: "string" },
            { name: "product_url", type: "string" },
          ],
        },
      }),

      // 3. Repeat pattern condition
      n("3", "condition", "Repeat buyer?", 520, {
        conditionData: [
          {
            variable: "is_repeat_buyer",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Post-experience delay
      n("4", "delayAction", "Wait for product experience (14 days)", 760, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 14, unit: "days" },
        },
      }),

      // 5. Subscription conversion email
      emailNode(
        "5",
        "Subscription conversion email",
        1000,
        "{customer_email}",
        "Save {discount_percent}% on {product_name} — subscribe and never run out",
        "Hi {customer_name},\\n\\nHow are you enjoying {product_name}? We hope it is everything you expected!\\n\\nDid you know you can save {discount_percent}% by subscribing?\\n\\nOne-Time Price: ${one_time_price}\\nSubscription Price: ${subscription_price}/delivery\\nAnnual Savings: ${annual_savings}\\n\\nSubscribe now and enjoy:\\n- {discount_percent}% off every order\\n- Free shipping on all deliveries\\n- Flexible schedule — skip or cancel anytime\\n\\nStart your subscription:\\n{product_url}?subscribe=true\\n\\nYou have already ordered {repeat_order_count} times — make it effortless!",
      ),

      // 6. Conversion tracking (runCode)
      n("6", "runCode", "Tag conversion attempt", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Track conversion attempt
return {
  conversion_status: "email_sent",
  conversion_channel: "email",
  conversion_type: vars.is_repeat_buyer === "yes" ? "repeat_to_sub" : "first_to_sub",
  timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "conversion_status", type: "string" },
            { name: "conversion_channel", type: "string" },
            { name: "conversion_type", type: "string" },
            { name: "timestamp", type: "string" },
          ],
        },
      }),

      // 7. Product conversion logging (Sheets)
      sheetsNode(
        "7",
        "Log conversion attempt (Sheets)",
        1480,
        "SubConversion",
        '[["{customer_email}","{product_name}","{one_time_price}","{subscription_price}","{annual_savings}","{is_repeat_buyer}","{repeat_order_count}","{conversion_type}","{timestamp}"]]',
      ),

      // 8. Growth ops alert
      slackNode(
        "8",
        "Slack: Growth ops notification",
        1720,
        ":chart_with_upwards_trend: Subscription Conversion Email Sent\\nCustomer: {customer_email}\\nProduct: {product_name}\\nType: {conversion_type}\\nRepeat Orders: {repeat_order_count}\\nPotential Annual Savings: ${annual_savings}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // repeat buyer -> faster nurture
      e("3", "4", "output_2"), // first buyer -> same delay, different context
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 540. SUBSCRIPTION ORDER CONFIRMATION (7 nodes)
  // ===================================================================

  "Subscription Order Confirmation": {
    summary:
      "Sends personalized subscription order confirmations with tenure milestone recognition. A recurring order webhook pulls order/billing/renewal details, checks for tenure milestones, sends celebration/reward emails for milestones, sends standard confirmations otherwise, delivers via Postmark for transactional reliability, and logs milestones to Sheets.",
    nodes: [
      trigger("Webhook: Recurring subscription order", "webhook_app", {
        source: "Subscription Platform",
        event: "subscription.order.created",
        howItStarts:
          "A recurring subscription order is created (auto-renewal billing successful), triggering a personalized confirmation with milestone recognition",
        setup:
          "Configure subscription platform to fire a webhook on each recurring order creation. Include order details, billing info, renewal date, customer tenure, and order count.",
        inputFields: [],
      }),

      // 2. Order/billing/renewal pull (runCode)
      n("2", "runCode", "Extract order and tenure details", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse recurring order data
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = payload.email || payload.customer_email || "";
const name = payload.customer_name || payload.name || "Valued Subscriber";
const orderId = payload.order_id || payload.id || "";
const orderTotal = payload.total || payload.order_total || "0";
const items = payload.items || payload.line_items || [];
const itemsSummary = items.map(i => (i.title || i.name) + " x" + (i.quantity || 1)).join(", ") || "subscription items";
const nextRenewal = payload.next_billing_date || payload.next_renewal || "";
const orderCount = parseInt(payload.order_count || payload.subscription_order_number || "1");
const tenureMonths = parseInt(payload.tenure_months || String(orderCount));
const milestones = [3, 6, 12, 24, 36, 48, 60];
const isMilestone = milestones.includes(orderCount) || milestones.includes(tenureMonths) ? "yes" : "no";
const milestoneLabel = tenureMonths >= 60 ? "5 Year" : tenureMonths >= 48 ? "4 Year" : tenureMonths >= 36 ? "3 Year" : tenureMonths >= 24 ? "2 Year" : tenureMonths >= 12 ? "1 Year" : orderCount >= 6 ? "6 Month" : orderCount >= 3 ? "3 Month" : "";
return {
  customer_email: email,
  customer_name: name,
  order_id: orderId,
  order_total: orderTotal,
  items_summary: itemsSummary,
  next_renewal: nextRenewal,
  order_count: String(orderCount),
  tenure_months: String(tenureMonths),
  is_milestone: isMilestone,
  milestone_label: milestoneLabel
};`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "order_id", type: "string" },
            { name: "order_total", type: "string" },
            { name: "items_summary", type: "string" },
            { name: "next_renewal", type: "string" },
            { name: "order_count", type: "string" },
            { name: "tenure_months", type: "string" },
            { name: "is_milestone", type: "string" },
            { name: "milestone_label", type: "string" },
          ],
        },
      }),

      // 3. Tenure milestone condition
      n("3", "condition", "Tenure milestone reached?", 520, {
        conditionData: [
          {
            variable: "is_milestone",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Milestone celebration + reward email (True path)
      emailNode(
        "4",
        "Milestone celebration email",
        760,
        "{customer_email}",
        "Congratulations on your {milestone_label} milestone, {customer_name}!",
        "Hi {customer_name},\\n\\nWow — {milestone_label} as a subscriber! Thank you for being part of our community.\\n\\nYour Latest Order (#{order_id}):\\n{items_summary}\\nTotal: ${order_total}\\n\\nTo celebrate this milestone, here is a special gift:\\nUse code LOYAL{order_count} for 20% off your next add-on order!\\n\\nNext renewal: {next_renewal}\\n\\nThank you for your continued trust in us.",
      ),

      // 5. Standard confirmation email (False path)
      emailNode(
        "5",
        "Standard order confirmation email",
        760,
        "{customer_email}",
        "Your subscription order #{order_id} is confirmed",
        "Hi {customer_name},\\n\\nYour subscription order has been processed successfully.\\n\\nOrder #{order_id}:\\n{items_summary}\\nTotal: ${order_total}\\n\\nNext renewal: {next_renewal}\\nOrder count: {order_count}\\n\\nManage your subscription anytime from your account dashboard.",
        400,
      ),

      // 6. Transactional send (Postmark)
      postmarkNode(
        "6",
        "Send via Postmark (transactional)",
        1000,
        "{customer_email}",
        "{postmark_order_confirmation_template}",
        `{"order_id":"{order_id}","items":"{items_summary}","total":"{order_total}","next_renewal":"{next_renewal}","customer_name":"{customer_name}"}`,
      ),

      // 7. Milestone logging (Sheets)
      sheetsNode(
        "7",
        "Log milestone data (Sheets)",
        1240,
        "SubMilestones",
        '[["{customer_email}","{customer_name}","{order_id}","{order_total}","{order_count}","{tenure_months}","{is_milestone}","{milestone_label}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // milestone -> celebration email
      e("3", "5", "output_2"), // standard -> normal confirmation
      e("4", "6"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 541. SUBSCRIPTION UPCOMING RENEWAL (8 nodes)
  // ===================================================================

  "Subscription Upcoming Renewal": {
    summary:
      "Proactive renewal communication with payment health checks. A daily renewal scan checks payment method health, routes on payment status, sends a 7-day courtesy notice, waits and sends a final reminder, sends payment update urgency for failed methods, and alerts billing ops on failures via Slack.",
    nodes: [
      trigger("Schedule: Daily renewal scan", "schedule", {
        schedule: "0 8 * * *",
        howItStarts:
          "Runs daily at 8 AM to identify subscriptions renewing in the next 7 days and check payment method health",
        setup:
          "Configure subscription platform API access. Set up payment processor credentials for payment method validation. Connect Slack for billing ops alerts.",
        inputFields: [],
      }),

      // 2. Payment method health check (hitApi)
      n("2", "hitApi", "Check payment method health", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{subscription_api}/api/v1/renewals/upcoming?days=7&include_payment_status=true",
          headers: [
            { key: "Authorization", value: "Bearer {subscription_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "renewals[0].customer_email",
              customName: "customer_email",
              type: "string",
            },
            {
              name: "renewals[0].customer_name",
              customName: "customer_name",
              type: "string",
            },
            {
              name: "renewals[0].renewal_date",
              customName: "renewal_date",
              type: "string",
            },
            {
              name: "renewals[0].amount",
              customName: "renewal_amount",
              type: "string",
            },
            {
              name: "renewals[0].plan_name",
              customName: "plan_name",
              type: "string",
            },
            {
              name: "renewals[0].payment_method.status",
              customName: "payment_status",
              type: "string",
            },
            {
              name: "renewals[0].payment_method.last4",
              customName: "card_last4",
              type: "string",
            },
            {
              name: "renewals[0].payment_method.exp_month",
              customName: "card_exp_month",
              type: "string",
            },
            {
              name: "renewals[0].payment_method.exp_year",
              customName: "card_exp_year",
              type: "string",
            },
            {
              name: "renewals.length",
              customName: "total_renewals",
              type: "number",
            },
          ],
        },
      }),

      // 3. Payment condition
      n("3", "condition", "Payment method healthy?", 520, {
        conditionData: [
          {
            variable: "payment_status",
            value: "valid",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. 7-day courtesy notice email (True — healthy payment)
      emailNode(
        "4",
        "7-day renewal courtesy notice",
        760,
        "{customer_email}",
        "Your {plan_name} subscription renews on {renewal_date}",
        "Hi {customer_name},\\n\\nJust a heads up — your {plan_name} subscription will renew on {renewal_date}.\\n\\nRenewal Amount: ${renewal_amount}\\nPayment Method: Card ending in {card_last4}\\n\\nNo action needed — your payment method is up to date. If you would like to make any changes to your subscription, you can do so from your account dashboard before {renewal_date}.\\n\\nThank you for being a subscriber!",
      ),

      // 5. Final reminder delay
      n("5", "delayAction", "Wait 4 days for final reminder", 1000, {
        delayDetails: {
          delayType: "fixed",
          fixedDelay: { value: 4, unit: "days" },
        },
      }),

      // 6. Final reminder email
      emailNode(
        "6",
        "Final renewal reminder (3 days out)",
        1240,
        "{customer_email}",
        "Reminder: {plan_name} renews in 3 days (${renewal_amount})",
        "Hi {customer_name},\\n\\nYour {plan_name} subscription renews in 3 days on {renewal_date}.\\n\\nAmount: ${renewal_amount}\\nCard: ending in {card_last4}\\n\\nEverything looks good on our end. If you need to make changes, please update your subscription settings before {renewal_date}.",
      ),

      // 7. Payment update urgent email (False — unhealthy payment)
      emailNode(
        "7",
        "Urgent: Update payment method",
        760,
        "{customer_email}",
        "Action Required: Update your payment method before {renewal_date}",
        "Hi {customer_name},\\n\\nYour {plan_name} subscription renews on {renewal_date}, but there is an issue with your payment method (card ending in {card_last4}, expires {card_exp_month}/{card_exp_year}).\\n\\nTo avoid any interruption to your subscription, please update your payment method now.\\n\\nRenewal Amount: ${renewal_amount}\\n\\nUpdate your payment method from your account settings. If you need assistance, reply to this email.",
        400,
      ),

      // 8. Billing ops failure alert (Slack)
      slackNode(
        "8",
        "Slack: Billing ops alert",
        1480,
        ":warning: Renewal Payment Issue Detected\\nCustomer: {customer_name} ({customer_email})\\nPlan: {plan_name} | Amount: ${renewal_amount}\\nRenewal Date: {renewal_date}\\nPayment Status: {payment_status}\\nCard: *{card_last4} (exp {card_exp_month}/{card_exp_year})\\nTotal Renewals Scanned: {total_renewals}",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // healthy -> courtesy notice
      e("3", "7", "output_2"), // unhealthy -> urgent update
      e("4", "5"),
      e("5", "6"),
      e("6", "8"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 542. SUPPLIER ORDER STATUS TRACKER (8 nodes)
  // ===================================================================

  "Supplier Order Status Tracker": {
    summary:
      "Monitors supplier PO status and escalates delays. A recurring poll fetches supplier status, compares state changes via runCode, routes on severity, triggers PagerDuty for critical delays, sends Slack warnings for non-critical changes, updates the procurement database in Sheets, and sends a meta-alert if the poll itself fails.",
    nodes: [
      trigger("Schedule: Recurring PO status poll", "schedule", {
        schedule: "0 */4 * * *",
        howItStarts:
          "Runs every 4 hours to poll all open purchase orders for status updates from suppliers",
        setup:
          "Configure supplier API credentials. Set up PagerDuty integration for critical alerts. Connect Slack for procurement team warnings. Prepare Google Sheets for PO tracking.",
        inputFields: [],
      }),

      // 2. Supplier status fetch
      n("2", "hitApi", "Fetch supplier PO statuses", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{supplier_api}/api/v1/purchase-orders?status=open&include_tracking=true",
          headers: [
            { key: "Authorization", value: "Bearer {supplier_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "orders[0].po_number",
              customName: "po_number",
              type: "string",
            },
            {
              name: "orders[0].supplier_name",
              customName: "supplier_name",
              type: "string",
            },
            {
              name: "orders[0].status",
              customName: "current_status",
              type: "string",
            },
            {
              name: "orders[0].expected_delivery",
              customName: "expected_delivery",
              type: "string",
            },
            {
              name: "orders[0].tracking_number",
              customName: "tracking_number",
              type: "string",
            },
            {
              name: "orders[0].total_value",
              customName: "po_value",
              type: "string",
            },
            {
              name: "orders[0].items_count",
              customName: "items_count",
              type: "number",
            },
            {
              name: "orders.length",
              customName: "total_open_pos",
              type: "number",
            },
          ],
        },
      }),

      // 3. State comparison (runCode)
      n("3", "runCode", "Compare state changes and assess severity", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compare PO status against expected timelines
const status = vars.current_status || "unknown";
const expectedDate = vars.expected_delivery || "";
const now = new Date();
const expected = expectedDate ? new Date(expectedDate) : null;
const daysUntilExpected = expected ? Math.floor((expected.getTime() - now.getTime()) / 86400000) : 999;
const isOverdue = daysUntilExpected < 0;
const daysOverdue = isOverdue ? Math.abs(daysUntilExpected) : 0;
const criticalStatuses = ["cancelled", "held", "suspended"];
const warningStatuses = ["delayed", "partial_shipped", "backordered"];
let severity = "info";
if (criticalStatuses.includes(status) || daysOverdue > 7) severity = "critical";
else if (warningStatuses.includes(status) || (daysOverdue > 0 && daysOverdue <= 7)) severity = "warning";
else if (status === "shipped" || status === "delivered") severity = "info";
return {
  severity: severity,
  days_until_expected: String(daysUntilExpected),
  days_overdue: String(daysOverdue),
  is_overdue: isOverdue ? "yes" : "no",
  status_summary: severity === "critical" ? "CRITICAL: PO " + vars.po_number + " is " + status + " (" + daysOverdue + " days overdue)" : severity === "warning" ? "WARNING: PO " + vars.po_number + " is " + status : "OK: PO " + vars.po_number + " is " + status,
  poll_success: "yes"
};`,
          outputVariables: [
            { name: "severity", type: "string" },
            { name: "days_until_expected", type: "string" },
            { name: "days_overdue", type: "string" },
            { name: "is_overdue", type: "string" },
            { name: "status_summary", type: "string" },
            { name: "poll_success", type: "string" },
          ],
        },
      }),

      // 4. Severity condition
      n("4", "condition", "Critical severity?", 760, {
        conditionData: [
          {
            variable: "severity",
            value: "critical",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. PagerDuty critical alert (True path)
      n("5", "hitApi", "PagerDuty: Critical PO alert", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://events.pagerduty.com/v2/enqueue",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "routing_key", value: "{pagerduty_routing_key}" },
            { key: "event_action", value: "trigger" },
            {
              key: "payload",
              value:
                '{"summary":"{status_summary}","severity":"critical","source":"supplier-tracker","custom_details":{"po_number":"{po_number}","supplier":"{supplier_name}","value":"{po_value}","days_overdue":"{days_overdue}"}}',
            },
          ],
          variables: [
            {
              name: "dedup_key",
              customName: "pagerduty_dedup_key",
              type: "string",
            },
            { name: "status", customName: "pagerduty_status", type: "string" },
          ],
        },
      }),

      // 6. Slack warning (False path — non-critical)
      slackNode(
        "6",
        "Slack: Procurement warning",
        1000,
        ":truck: PO Status Update\\n{status_summary}\\nSupplier: {supplier_name}\\nPO: {po_number} | Value: ${po_value}\\nExpected: {expected_delivery} | Tracking: {tracking_number}\\nOpen POs: {total_open_pos}",
        400,
      ),

      // 7. Procurement database update (Sheets)
      sheetsNode(
        "7",
        "Update procurement database (Sheets)",
        1240,
        "POTracking",
        '[["{po_number}","{supplier_name}","{current_status}","{severity}","{expected_delivery}","{days_overdue}","{po_value}","{tracking_number}","{status_summary}"]]',
      ),

      // 8. Meta-alert on poll failure
      slackNode(
        "8",
        "Slack: Poll failure meta-alert",
        1480,
        ":rotating_light: Supplier Status Poll Complete\\nTotal Open POs: {total_open_pos}\\nPoll Status: {poll_success}\\nCritical Issues: Check PagerDuty for escalations",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // critical -> PagerDuty
      e("4", "6", "output_2"), // non-critical -> Slack warning
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 543. SUPPLIER REORDER AUTOMATION (9 nodes)
  // ===================================================================

  "Supplier Reorder Automation": {
    summary:
      "Automates purchase order creation when inventory hits reorder points. A reorder point trigger evaluates suppliers via runCode, selects the best supplier, calculates optimal quantity, checks approval requirements, generates a PO, submits to the supplier via email, updates projected inventory in Sheets, and notifies procurement via Slack.",
    nodes: [
      trigger("Webhook: Reorder point reached", "webhook_app", {
        source: "Inventory Management System",
        event: "inventory.reorder_point",
        howItStarts:
          "An inventory item drops below its reorder point threshold, triggering automated supplier evaluation and PO generation",
        setup:
          "Configure inventory system to fire a webhook when stock drops below reorder point. Include product details, current stock, reorder point, supplier list, and demand forecast.",
        inputFields: [],
      }),

      // 2. Supplier evaluation (runCode)
      n("2", "runCode", "Evaluate and rank suppliers", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse reorder event and evaluate suppliers
const payload = JSON.parse(vars.webhook_payload || "{}");
const productName = payload.product_name || payload.sku_name || "";
const sku = payload.sku || payload.product_sku || "";
const currentStock = parseInt(payload.current_stock || "0");
const reorderPoint = parseInt(payload.reorder_point || "0");
const dailyDemand = parseFloat(payload.daily_demand || payload.avg_daily_sales || "1");
const suppliers = payload.suppliers || [];
// Score suppliers by price, lead time, reliability
const scored = suppliers.map(s => ({
  ...s,
  score: (100 - (s.unit_price || 0) * 10) + (100 - (s.lead_time_days || 14) * 3) + ((s.reliability_pct || 90) * 0.5)
})).sort((a, b) => b.score - a.score);
const bestSupplier = scored[0] || { name: "Default Supplier", email: "", unit_price: 0, lead_time_days: 14, moq: 1 };
return {
  product_name: productName,
  sku: sku,
  current_stock: String(currentStock),
  reorder_point: String(reorderPoint),
  daily_demand: String(dailyDemand),
  stock_deficit: String(Math.max(0, reorderPoint - currentStock)),
  best_supplier_name: bestSupplier.name || "Default Supplier",
  best_supplier_email: bestSupplier.email || "",
  unit_price: String(bestSupplier.unit_price || 0),
  lead_time_days: String(bestSupplier.lead_time_days || 14),
  moq: String(bestSupplier.moq || bestSupplier.minimum_order_quantity || 1),
  supplier_count: String(scored.length)
};`,
          outputVariables: [
            { name: "product_name", type: "string" },
            { name: "sku", type: "string" },
            { name: "current_stock", type: "string" },
            { name: "reorder_point", type: "string" },
            { name: "daily_demand", type: "string" },
            { name: "stock_deficit", type: "string" },
            { name: "best_supplier_name", type: "string" },
            { name: "best_supplier_email", type: "string" },
            { name: "unit_price", type: "string" },
            { name: "lead_time_days", type: "string" },
            { name: "moq", type: "string" },
            { name: "supplier_count", type: "string" },
          ],
        },
      }),

      // 3. Supplier selection confirmation
      n("3", "runCode", "Select supplier and build PO draft", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Confirm supplier selection and draft PO
const supplierName = vars.best_supplier_name;
const supplierEmail = vars.best_supplier_email;
return {
  selected_supplier: supplierName,
  supplier_email: supplierEmail,
  selection_reason: "Best score based on price ($" + vars.unit_price + "), lead time (" + vars.lead_time_days + " days), and reliability"
};`,
          outputVariables: [
            { name: "selected_supplier", type: "string" },
            { name: "supplier_email", type: "string" },
            { name: "selection_reason", type: "string" },
          ],
        },
      }),

      // 4. Quantity calculation (runCode)
      n("4", "runCode", "Calculate optimal order quantity", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate Economic Order Quantity (simplified)
const dailyDemand = parseFloat(vars.daily_demand || "1");
const leadTimeDays = parseInt(vars.lead_time_days || "14");
const moq = parseInt(vars.moq || "1");
const safetyStockDays = 7;
const coverageDays = leadTimeDays + safetyStockDays + 30; // cover lead time + safety + 30 days
const optimalQty = Math.ceil(dailyDemand * coverageDays);
const orderQty = Math.max(optimalQty, moq);
const orderValue = (orderQty * parseFloat(vars.unit_price || "0")).toFixed(2);
const needsApproval = parseFloat(orderValue) > 5000 ? "yes" : "no";
return {
  order_quantity: String(orderQty),
  order_value: orderValue,
  coverage_days: String(coverageDays),
  needs_approval: needsApproval,
  po_number: "PO-" + Date.now().toString(36).toUpperCase()
};`,
          outputVariables: [
            { name: "order_quantity", type: "string" },
            { name: "order_value", type: "string" },
            { name: "coverage_days", type: "string" },
            { name: "needs_approval", type: "string" },
            { name: "po_number", type: "string" },
          ],
        },
      }),

      // 5. Approval condition
      n("5", "condition", "Auto-approved (under $5,000)?", 1000, {
        conditionData: [
          {
            variable: "needs_approval",
            value: "no",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. PO generation (runCode)
      n("6", "runCode", "Generate purchase order document", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate structured PO
return {
  po_document: "PURCHASE ORDER " + vars.po_number + "\\nSupplier: " + vars.selected_supplier + "\\nSKU: " + vars.sku + "\\nProduct: " + vars.product_name + "\\nQuantity: " + vars.order_quantity + " units\\nUnit Price: $" + vars.unit_price + "\\nTotal: $" + vars.order_value + "\\nExpected Delivery: " + vars.lead_time_days + " days\\nSelection: " + vars.selection_reason,
  po_status: "generated"
};`,
          outputVariables: [
            { name: "po_document", type: "string" },
            { name: "po_status", type: "string" },
          ],
        },
      }),

      // 7. Supplier submission (email)
      emailNode(
        "7",
        "Submit PO to supplier",
        1480,
        "{supplier_email}",
        "Purchase Order {po_number} — {product_name} x{order_quantity}",
        "Dear {selected_supplier},\\n\\nPlease find below our purchase order:\\n\\n{po_document}\\n\\nPlease confirm receipt and expected ship date by replying to this email.\\n\\nThank you for your partnership.",
      ),

      // 8. Projected inventory update (Sheets)
      sheetsNode(
        "8",
        "Update projected inventory (Sheets)",
        1720,
        "InventoryPO",
        '[["{po_number}","{sku}","{product_name}","{selected_supplier}","{order_quantity}","{unit_price}","{order_value}","{lead_time_days}","{coverage_days}","{needs_approval}","{po_status}"]]',
      ),

      // 9. Procurement Slack notification
      slackNode(
        "9",
        "Slack: Procurement notification",
        1960,
        ":clipboard: Purchase Order Generated\\nPO: {po_number}\\nProduct: {product_name} ({sku})\\nSupplier: {selected_supplier}\\nQty: {order_quantity} | Value: ${order_value}\\nCurrent Stock: {current_stock} | Reorder Point: {reorder_point}\\nApproval: {needs_approval} | Coverage: {coverage_days} days",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // auto-approved -> generate PO
      e("5", "9", "output_2"), // needs approval -> notify procurement (skip PO generation)
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 544. THANK-YOU AND BRAND APPRECIATION (6 nodes)
  // ===================================================================

  "Thank-You and Brand Appreciation": {
    summary:
      "Sends personalized thank-you messages to first-time customers with tier-based messaging. A first order trigger pulls customer and order details, checks order value for premium vs. standard tiers, sends a premium appreciation email for high-value orders, sends a standard thank-you for others, and alerts marketing ops on failures.",
    nodes: [
      trigger("Webhook: First order completed", "webhook_app", {
        source: "E-Commerce Platform",
        event: "order.created",
        howItStarts:
          "A customer completes their first-ever order, triggering a personalized brand appreciation message based on order value",
        setup:
          "Configure order webhook filtered for first-time customers. Include order details, customer name, email, and order value.",
        inputFields: [],
      }),

      // 2. Customer/order pull (runCode)
      n("2", "runCode", "Extract customer and order details", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse first order data
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = payload.email || payload.customer_email || "";
const name = payload.customer_name || payload.name || payload.first_name || "there";
const orderId = payload.order_id || payload.id || "";
const orderTotal = parseFloat(payload.total || payload.order_total || "0");
const items = payload.items || payload.line_items || [];
const itemNames = items.map(i => i.title || i.name).filter(Boolean).join(", ") || "your selection";
const valueThreshold = 100;
const valueTier = orderTotal >= valueThreshold ? "premium" : "standard";
return {
  customer_email: email,
  customer_name: name,
  order_id: orderId,
  order_total: String(orderTotal.toFixed(2)),
  items_summary: itemNames,
  item_count: String(items.length),
  value_tier: valueTier
};`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "order_id", type: "string" },
            { name: "order_total", type: "string" },
            { name: "items_summary", type: "string" },
            { name: "item_count", type: "string" },
            { name: "value_tier", type: "string" },
          ],
        },
      }),

      // 3. Value condition
      n("3", "condition", "Premium order value?", 520, {
        conditionData: [
          {
            variable: "value_tier",
            value: "premium",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Premium appreciation email (True path)
      emailNode(
        "4",
        "Premium brand appreciation email",
        760,
        "{customer_email}",
        "Welcome to the family, {customer_name} — a special thank you",
        "Dear {customer_name},\\n\\nThank you so much for your generous first order (#{order_id}, ${order_total}).\\n\\nYour selection: {items_summary}\\n\\nAs a token of our appreciation, we would like to offer you:\\n- Free express shipping on your next order (code: WELCOME-VIP)\\n- Early access to new arrivals\\n- A dedicated customer care contact\\n\\nWe are thrilled to have you join our community. If there is anything we can do to make your experience even better, please do not hesitate to reach out.\\n\\nWith gratitude,\\nThe Team",
      ),

      // 5. Standard thank-you email (False path)
      emailNode(
        "5",
        "Standard thank-you email",
        760,
        "{customer_email}",
        "Thank you for your first order, {customer_name}!",
        "Hi {customer_name},\\n\\nThank you for choosing us! Your order (#{order_id}) is being prepared.\\n\\nYour items: {items_summary}\\nTotal: ${order_total}\\n\\nAs a welcome gift, enjoy 10% off your next order with code WELCOME10.\\n\\nWe hope this is the start of something great. Welcome aboard!",
        400,
      ),

      // 6. Marketing ops failure alert
      slackNode(
        "6",
        "Slack: Marketing ops alert",
        1000,
        ":wave: First-Order Thank-You Sent\\nCustomer: {customer_name} ({customer_email})\\nOrder: #{order_id} | Value: ${order_total}\\nTier: {value_tier} | Items: {item_count}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // premium -> VIP appreciation
      e("3", "5", "output_2"), // standard -> standard thank you
      e("4", "6"),
      e("5", "6"),
    ],
  },

  // ===================================================================
  // 545. TRADE-IN QUOTE GENERATOR BOT (9 nodes)
  // ===================================================================

  "Trade-In Quote Generator Bot": {
    summary:
      "Automated trade-in valuation from inquiry to quote with condition scoring. A trade-in inquiry triggers product identification, pulls market values, scores condition via AI, calculates quotes via runCode, routes on eligibility (quote/recycle/ineligible), creates trade-in records and shipping labels, responds via chat channel, and alerts ops on anomalies via Slack.",
    nodes: [
      trigger("Webhook: Trade-in inquiry", "webhook_app", {
        source: "Chat / Web Form",
        event: "tradein.inquiry.submitted",
        howItStarts:
          "A customer submits a trade-in inquiry with product details and condition description, triggering automated valuation",
        setup:
          "Configure chat widget or web form to submit trade-in inquiries. Include product description, condition notes, photos (optional), and customer contact details.",
        inputFields: [],
      }),

      // 2. Product identification (AI)
      aiNode(
        "2",
        "AI product identification",
        280,
        "You are a product identification specialist. From the trade-in inquiry, identify the exact product model, brand, category, and any relevant specifications (storage, color, size). Extract the customer contact info and condition description. Respond JSON: {brand: string, model: string, category: string, specs: string, condition_description: string, customer_email: string, customer_name: string, customer_phone: string, has_photos: boolean, inquiry_text: string}",
        "{webhook_payload}",
        [
          { field: "brand", as: "product_brand", type: "string" },
          { field: "model", as: "product_model", type: "string" },
          { field: "category", as: "product_category", type: "string" },
          { field: "specs", as: "product_specs", type: "string" },
          {
            field: "condition_description",
            as: "condition_description",
            type: "string",
          },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_phone", as: "customer_phone", type: "string" },
          { field: "has_photos", as: "has_photos", type: "boolean" },
        ],
      ),

      // 3. Market value pull
      n("3", "hitApi", "Pull market trade-in values", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{valuation_api}/api/v1/values?brand={product_brand}&model={product_model}&category={product_category}",
          headers: [
            { key: "Authorization", value: "Bearer {valuation_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "market_value.excellent",
              customName: "value_excellent",
              type: "string",
            },
            {
              name: "market_value.good",
              customName: "value_good",
              type: "string",
            },
            {
              name: "market_value.fair",
              customName: "value_fair",
              type: "string",
            },
            {
              name: "market_value.recycle",
              customName: "value_recycle",
              type: "string",
            },
            {
              name: "market_value.eligible",
              customName: "product_eligible",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI condition scoring
      aiNode(
        "4",
        "AI condition scoring",
        760,
        "You are a product condition assessor. Based on the customer description and product details, score the condition as excellent, good, fair, or recycle. Consider scratches, functionality, battery health (electronics), completeness of accessories. Be conservative in your assessment. Respond JSON: {condition_grade: string, condition_score: number, grade_reasoning: string, deductions: string, confidence: string}",
        "Product: {product_brand} {product_model} ({product_category}). Specs: {product_specs}. Customer description: {condition_description}. Has photos: {has_photos}. Market values — Excellent: ${value_excellent}, Good: ${value_good}, Fair: ${value_fair}, Recycle: ${value_recycle}.",
        [
          { field: "condition_grade", as: "condition_grade", type: "string" },
          { field: "condition_score", as: "condition_score", type: "number" },
          { field: "grade_reasoning", as: "grade_reasoning", type: "string" },
          { field: "deductions", as: "deductions", type: "string" },
          { field: "confidence", as: "assessment_confidence", type: "string" },
        ],
      ),

      // 5. Quote calculation (runCode)
      n("5", "runCode", "Calculate trade-in quote", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate final trade-in quote based on condition grade
const grade = vars.condition_grade || "fair";
const eligible = vars.product_eligible || "yes";
const values = {
  excellent: parseFloat(vars.value_excellent || "0"),
  good: parseFloat(vars.value_good || "0"),
  fair: parseFloat(vars.value_fair || "0"),
  recycle: parseFloat(vars.value_recycle || "0")
};
let quoteAmount = values[grade] || values.fair || 0;
let quoteStatus = "eligible";
if (eligible === "no" || quoteAmount <= 0) {
  quoteStatus = "ineligible";
  quoteAmount = 0;
} else if (grade === "recycle" || quoteAmount < 10) {
  quoteStatus = "recycle";
  quoteAmount = values.recycle || 5;
}
const quoteId = "TI-" + Date.now().toString(36).toUpperCase();
return {
  quote_amount: String(quoteAmount.toFixed(2)),
  quote_status: quoteStatus,
  quote_id: quoteId,
  quote_valid_days: "14",
  quote_summary: quoteStatus === "eligible" ? "Your " + vars.product_brand + " " + vars.product_model + " (condition: " + grade + ") qualifies for a $" + quoteAmount.toFixed(2) + " trade-in credit." : quoteStatus === "recycle" ? "Your device qualifies for our recycling program with a $" + quoteAmount.toFixed(2) + " credit." : "Unfortunately, this product is not eligible for our trade-in program at this time."
};`,
          outputVariables: [
            { name: "quote_amount", type: "string" },
            { name: "quote_status", type: "string" },
            { name: "quote_id", type: "string" },
            { name: "quote_valid_days", type: "string" },
            { name: "quote_summary", type: "string" },
          ],
        },
      }),

      // 6. Quote/recycle/ineligible condition
      n("6", "condition", "Eligible for trade-in?", 1240, {
        conditionData: [
          {
            variable: "quote_status",
            value: "eligible",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Trade-in record + label creation (True path)
      n("7", "hitApi", "Create trade-in record + shipping label", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://{tradein_api}/api/v1/trade-ins",
          headers: [
            { key: "Authorization", value: "Bearer {tradein_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "quote_id", value: "{quote_id}" },
            { key: "customer_email", value: "{customer_email}" },
            { key: "customer_name", value: "{customer_name}" },
            { key: "product", value: "{product_brand} {product_model}" },
            { key: "condition_grade", value: "{condition_grade}" },
            { key: "quote_amount", value: "{quote_amount}" },
            { key: "generate_label", value: "true" },
          ],
          variables: [
            {
              name: "trade_in.id",
              customName: "tradein_record_id",
              type: "string",
            },
            {
              name: "trade_in.shipping_label_url",
              customName: "shipping_label_url",
              type: "string",
            },
            {
              name: "trade_in.status",
              customName: "tradein_status",
              type: "string",
            },
          ],
        },
      }),

      // 8. Channel response (sendMessage)
      n("8", "sendMessage", "Respond to customer with quote", 1720, {
        sendMessageDetails: {
          content:
            "{quote_summary}\\n\\nQuote ID: {quote_id}\\nCondition: {condition_grade}\\nAssessment: {grade_reasoning}\\n\\nThis quote is valid for {quote_valid_days} days. To proceed, we will send a prepaid shipping label to {customer_email}.",
        },
      }),

      // 9. Ops anomaly alert (Slack)
      slackNode(
        "9",
        "Slack: Trade-in ops alert",
        1960,
        ":recycle: Trade-In Quote Generated\\nQuote: {quote_id} | Status: {quote_status}\\nProduct: {product_brand} {product_model} ({product_category})\\nCondition: {condition_grade} (confidence: {assessment_confidence})\\nQuote: ${quote_amount}\\nCustomer: {customer_name} ({customer_email})",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // eligible -> create record + label
      e("6", "8", "output_2"), // ineligible/recycle -> respond directly
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 546. TRIAL-TO-PAID CHECKOUT ABANDONMENT (9 nodes)
  // ===================================================================

  "Trial-to-Paid Checkout Abandonment": {
    summary:
      "Multi-channel recovery for trial users who abandon the paid upgrade checkout. A trial upgrade abandon event pulls trial usage data, generates an AI ROI summary, routes on usage intensity, sends urgency or feature-discovery emails, offers a trial extension, sends an SMS reminder via Twilio, and logs growth ops data to Sheets.",
    nodes: [
      trigger("Webhook: Trial upgrade checkout abandoned", "webhook_app", {
        source: "SaaS Platform",
        event: "checkout.trial_upgrade.abandoned",
        howItStarts:
          "A trial user starts the paid plan upgrade checkout but abandons before completing payment, triggering a multi-channel recovery series",
        setup:
          "Configure SaaS platform to detect trial upgrade checkout abandonments. Include trial usage metrics, plan details, customer email, and checkout recovery URL.",
        inputFields: [],
      }),

      // 2. Trial usage pull (runCode)
      n("2", "runCode", "Extract trial usage metrics", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse trial upgrade abandonment data
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = payload.email || payload.customer_email || "";
const name = payload.name || payload.customer_name || "";
const phone = payload.phone || payload.mobile || "";
const planName = payload.plan_name || payload.target_plan || "";
const planPrice = payload.plan_price || payload.price || "0";
const trialDaysUsed = parseInt(payload.trial_days_used || payload.days_active || "0");
const trialDaysTotal = parseInt(payload.trial_days_total || "14");
const trialDaysRemaining = trialDaysTotal - trialDaysUsed;
const featuresUsed = parseInt(payload.features_used || "0");
const totalFeatures = parseInt(payload.total_features || "10");
const sessionsCount = parseInt(payload.sessions || payload.login_count || "0");
const recoveryUrl = payload.recovery_url || payload.checkout_url || "";
const usagePercent = totalFeatures > 0 ? Math.round((featuresUsed / totalFeatures) * 100) : 0;
const intensity = usagePercent >= 60 ? "high" : usagePercent >= 30 ? "medium" : "low";
return {
  customer_email: email,
  customer_name: name,
  customer_phone: phone,
  plan_name: planName,
  plan_price: planPrice,
  trial_days_used: String(trialDaysUsed),
  trial_days_remaining: String(trialDaysRemaining),
  features_used: String(featuresUsed),
  total_features: String(totalFeatures),
  usage_percent: String(usagePercent),
  usage_intensity: intensity,
  sessions_count: String(sessionsCount),
  recovery_url: recoveryUrl
};`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "customer_phone", type: "string" },
            { name: "plan_name", type: "string" },
            { name: "plan_price", type: "string" },
            { name: "trial_days_used", type: "string" },
            { name: "trial_days_remaining", type: "string" },
            { name: "features_used", type: "string" },
            { name: "total_features", type: "string" },
            { name: "usage_percent", type: "string" },
            { name: "usage_intensity", type: "string" },
            { name: "sessions_count", type: "string" },
            { name: "recovery_url", type: "string" },
          ],
        },
      }),

      // 3. AI ROI summary
      aiNode(
        "3",
        "AI generate ROI summary",
        520,
        "You are a SaaS conversion specialist. Based on trial usage data, generate a personalized ROI summary showing the value the user has already gained and what they would lose without upgrading. Focus on features used, time invested, and potential productivity gains. Respond JSON: {roi_summary: string, top_feature_highlight: string, loss_statement: string, urgency_subject: string, discovery_subject: string, extension_offer: string}",
        "User: {customer_name}. Plan: {plan_name} at ${plan_price}/mo. Trial: {trial_days_used} of {trial_days_remaining} days remaining. Features used: {features_used}/{total_features} ({usage_percent}%). Sessions: {sessions_count}. Usage intensity: {usage_intensity}.",
        [
          { field: "roi_summary", as: "roi_summary", type: "string" },
          {
            field: "top_feature_highlight",
            as: "top_feature_highlight",
            type: "string",
          },
          { field: "loss_statement", as: "loss_statement", type: "string" },
          { field: "urgency_subject", as: "urgency_subject", type: "string" },
          {
            field: "discovery_subject",
            as: "discovery_subject",
            type: "string",
          },
          { field: "extension_offer", as: "extension_offer", type: "string" },
        ],
      ),

      // 4. Usage intensity condition
      n("4", "condition", "High usage intensity?", 760, {
        conditionData: [
          {
            variable: "usage_intensity",
            value: "high",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
          {
            variable: "usage_intensity",
            value: "medium",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
        ],
      }),

      // 5. Urgency email (True — high/medium usage)
      emailNode(
        "5",
        "Urgency: Don't lose your progress email",
        1000,
        "{customer_email}",
        "{urgency_subject}",
        "Hi {customer_name},\\n\\n{roi_summary}\\n\\nYou have used {features_used} out of {total_features} features across {sessions_count} sessions — that is real progress.\\n\\n{loss_statement}\\n\\n{top_feature_highlight}\\n\\nUpgrade now and keep everything you have built:\\n{recovery_url}\\n\\nYour trial has {trial_days_remaining} days remaining.",
      ),

      // 6. Feature-discovery email (False — low usage)
      emailNode(
        "6",
        "Discovery: Unlock more features email",
        1000,
        "{customer_email}",
        "{discovery_subject}",
        "Hi {customer_name},\\n\\nWe noticed you have been exploring {plan_name} — great start!\\n\\nYou have discovered {features_used} of {total_features} features so far. Here is what you might be missing:\\n\\n{top_feature_highlight}\\n\\n{extension_offer}\\n\\nContinue your upgrade:\\n{recovery_url}",
        400,
      ),

      // 7. Extension offer email
      emailNode(
        "7",
        "Trial extension offer email",
        1240,
        "{customer_email}",
        "We extended your {plan_name} trial — {extension_offer}",
        "Hi {customer_name},\\n\\nWe want to make sure you have enough time to experience everything {plan_name} has to offer.\\n\\n{extension_offer}\\n\\nYour trial has been extended. Take your time exploring, and upgrade whenever you are ready:\\n{recovery_url}\\n\\nQuestions? Reply to this email — our team is here to help.",
      ),

      // 8. SMS reminder (Twilio)
      smsNode(
        "8",
        "SMS upgrade reminder",
        1480,
        "{customer_phone}",
        "Hi {customer_name}, your {plan_name} trial is ending soon. You have used {features_used} features — don't lose access! Upgrade: {recovery_url}",
      ),

      // 9. Growth ops logging (Sheets)
      sheetsNode(
        "9",
        "Log growth ops data (Sheets)",
        1720,
        "TrialRecovery",
        '[["{customer_email}","{plan_name}","{plan_price}","{trial_days_used}","{trial_days_remaining}","{usage_percent}","{usage_intensity}","{features_used}","{sessions_count}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // high/medium usage -> urgency email
      e("4", "6", "output_2"), // low usage -> discovery email
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 547. UGC OR PHOTO REVIEW REQUEST (8 nodes)
  // ===================================================================

  "UGC or Photo Review Request": {
    summary:
      "Solicits user-generated content with media-type-specific messaging and loyalty rewards. A post-delivery trigger (1 week) assesses visual value via runCode, routes on media type suitability, sends photo review, video review, or text review emails, credits loyalty points for submissions, and logs UGC ops data to Sheets.",
    nodes: [
      trigger("Webhook: Order delivered (1 week ago)", "webhook_app", {
        source: "E-Commerce Platform",
        event: "order.delivered",
        howItStarts:
          "An order delivery is confirmed and 7 days have passed, triggering a UGC/review request with media-type-specific messaging based on product visual value",
        setup:
          "Configure fulfillment webhook with a 7-day delay after delivery confirmation. Include product details, customer email, and product category for visual value assessment.",
        inputFields: [],
      }),

      // 2. Product visual value assessment (runCode)
      n("2", "runCode", "Assess product visual value", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse delivery data and assess visual value for UGC type recommendation
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = payload.email || payload.customer_email || "";
const name = payload.customer_name || payload.name || "there";
const orderId = payload.order_id || payload.id || "";
const items = payload.items || payload.line_items || [];
const product = items[0] || {};
const productName = product.title || product.name || "your purchase";
const productId = product.id || product.product_id || "";
const category = (product.category || product.product_type || "").toLowerCase();
const price = parseFloat(product.price || "0");
// Visual value scoring
const highVisualCategories = ["clothing", "fashion", "jewelry", "shoes", "furniture", "home_decor", "art", "beauty", "cosmetics"];
const videoCategories = ["electronics", "gadgets", "fitness", "kitchen", "tools", "toys"];
const isHighVisual = highVisualCategories.some(c => category.includes(c));
const isVideoWorthy = videoCategories.some(c => category.includes(c));
let mediaType = "text";
if (isHighVisual && price >= 50) mediaType = "photo";
else if (isVideoWorthy && price >= 75) mediaType = "video";
else if (isHighVisual || price >= 30) mediaType = "photo";
const loyaltyPoints = mediaType === "video" ? 500 : mediaType === "photo" ? 300 : 100;
const reviewUrl = product.review_url || "https://{store_domain}/review/" + productId;
return {
  customer_email: email,
  customer_name: name,
  order_id: orderId,
  product_name: productName,
  product_id: productId,
  product_category: category,
  product_price: String(price.toFixed(2)),
  media_type: mediaType,
  loyalty_points: String(loyaltyPoints),
  review_url: reviewUrl
};`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "order_id", type: "string" },
            { name: "product_name", type: "string" },
            { name: "product_id", type: "string" },
            { name: "product_category", type: "string" },
            { name: "product_price", type: "string" },
            { name: "media_type", type: "string" },
            { name: "loyalty_points", type: "string" },
            { name: "review_url", type: "string" },
          ],
        },
      }),

      // 3. Media type condition
      n("3", "condition", "Photo-worthy product?", 520, {
        conditionData: [
          {
            variable: "media_type",
            value: "photo",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
          {
            variable: "media_type",
            value: "video",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
        ],
      }),

      // 4. Photo review email (True — photo/video worthy)
      emailNode(
        "4",
        "Photo/Video review request email",
        760,
        "{customer_email}",
        "Show off your {product_name} — earn {loyalty_points} points!",
        "Hi {customer_name},\\n\\nWe hope you are loving your {product_name}!\\n\\nYour experience matters — and so does how it looks! Share a photo or video of your {product_name} in action and earn {loyalty_points} loyalty points.\\n\\nHow to share:\\n1. Snap a photo or short video\\n2. Upload it with your review at: {review_url}\\n3. Points are credited automatically within 24 hours\\n\\nYour visual review helps other shoppers make confident decisions. Thank you for being part of our community!",
      ),

      // 5. Video review email (for video-worthy but handled via same path, with specific CTA)
      emailNode(
        "5",
        "Video review request email",
        760,
        "{customer_email}",
        "Share a quick video review of {product_name} — earn {loyalty_points} points!",
        "Hi {customer_name},\\n\\nHow is your {product_name} working out?\\n\\nWe would love to see it in action! Record a quick 30-60 second video showing how you use your {product_name} and earn {loyalty_points} loyalty points.\\n\\nTips for a great video review:\\n- Show the product in use\\n- Mention your favorite feature\\n- Keep it natural — no script needed!\\n\\nUpload here: {review_url}\\n\\nVideo reviews are incredibly helpful for other customers. Thank you!",
        400,
      ),

      // 6. Text review email (False path)
      emailNode(
        "6",
        "Text review request email",
        1000,
        "{customer_email}",
        "How is your {product_name}? Share a review, earn {loyalty_points} points",
        "Hi {customer_name},\\n\\nYou have had your {product_name} for about a week now — we would love to hear your thoughts!\\n\\nLeave a quick written review and earn {loyalty_points} loyalty points:\\n{review_url}\\n\\nEven a few sentences help other customers make great choices. Thank you!",
      ),

      // 7. Loyalty points credit (hitApi)
      n("7", "hitApi", "Credit loyalty points", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{loyalty_api}/api/v1/points/credit",
          headers: [
            { key: "Authorization", value: "Bearer {loyalty_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "customer_email", value: "{customer_email}" },
            { key: "points", value: "{loyalty_points}" },
            {
              key: "reason",
              value: "UGC review request - {media_type} - Order #{order_id}",
            },
            { key: "pending", value: "true" },
          ],
          variables: [
            {
              name: "points_balance",
              customName: "new_points_balance",
              type: "number",
            },
            {
              name: "credit_id",
              customName: "points_credit_id",
              type: "string",
            },
          ],
        },
      }),

      // 8. UGC ops logging (Sheets)
      sheetsNode(
        "8",
        "Log UGC request (Sheets)",
        1480,
        "UGCOps",
        '[["{customer_email}","{product_name}","{product_category}","{product_price}","{media_type}","{loyalty_points}","{order_id}","{review_url}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // photo/video worthy -> photo review email
      e("3", "6", "output_2"), // text only -> text review email
      e("4", "5"), // photo -> also offer video
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
    ],
  },
};
